/**
 * Redesigned site chrome behavior (redesign spec §3.1, §3.4).
 * Markup: views/redesign/partials/{navbar,banner}.ejs.
 *
 * Menus: each .nav-sub-btn toggles its .subnav (click, or Enter/Space, which a
 * <button> already turns into a click). One open at a time. Escape closes the
 * open dropdown and returns focus to its button; an outside click closes it.
 * At 1120px and below the Menu button shows the nav as a panel and the
 * dropdowns open in place. In the panel the first Escape closes a dropdown,
 * the next closes the panel and focuses the Menu button. Following a link
 * closes everything. The page behind the open panel does not scroll. No focus
 * trap: the panel sits in the normal tab order.
 *
 * Banner: shows the pause button (hidden without JS, where the ticker still
 * pauses on hover and focus) and toggles .is-paused.
 *
 * Bundled globally; does nothing when the redesigned chrome is absent.
 */

const MOBILE_QUERY = '(max-width: 1120px)';

function initNav(header) {
    const nav = header.querySelector('.primary-nav');
    const menuBtn = header.querySelector('.menu-btn');
    if (!nav) return;

    const subButtons = Array.from(nav.querySelectorAll('.nav-sub-btn'));
    const panelFor = (btn) => document.getElementById(btn.getAttribute('aria-controls'));
    const mobile = window.matchMedia(MOBILE_QUERY);

    const openSubButton = () => subButtons.find((b) => b.getAttribute('aria-expanded') === 'true');
    const menuIsOpen = () => nav.classList.contains('open');

    function setSub(btn, open) {
        const panel = panelFor(btn);
        btn.setAttribute('aria-expanded', String(open));
        if (panel) panel.hidden = !open;
    }

    function closeSubs(except) {
        subButtons.forEach((b) => {
            if (b !== except) setSub(b, false);
        });
    }

    function setMenu(open) {
        if (!menuBtn) return;
        nav.classList.toggle('open', open);
        menuBtn.setAttribute('aria-expanded', String(open));
        // Above the sticky point the banner and utility bar push the panel
        // down, so CSS alone can't fit it to the screen. Size it from where
        // it actually starts, so its last links stay reachable.
        if (open) {
            nav.style.maxHeight = `${Math.max(window.innerHeight - nav.getBoundingClientRect().top, 200)}px`;
        } else {
            nav.style.maxHeight = '';
        }
        document.documentElement.classList.toggle('rd-nav-locked', open);
        if (!open) closeSubs();
    }

    function closeAll() {
        closeSubs();
        if (menuIsOpen()) setMenu(false);
    }

    subButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
            const open = btn.getAttribute('aria-expanded') !== 'true';
            closeSubs(btn);
            setSub(btn, open);
        });
    });

    if (menuBtn) {
        menuBtn.addEventListener('click', () => setMenu(!menuIsOpen()));
    }

    nav.addEventListener('click', (e) => {
        if (e.target.closest('a')) closeAll();
    });

    document.addEventListener('click', (e) => {
        if (header.contains(e.target)) return;
        if (openSubButton() && !mobile.matches) closeSubs();
        if (menuIsOpen()) setMenu(false);
    });

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const btn = openSubButton();
        if (btn) {
            setSub(btn, false);
            btn.focus();
            return;
        }
        if (menuIsOpen()) {
            setMenu(false);
            menuBtn.focus();
        }
    });

    // Back above the breakpoint the panel no longer exists; reset its state.
    mobile.addEventListener('change', (e) => {
        if (!e.matches) {
            closeAll();
            document.documentElement.classList.remove('rd-nav-locked');
        }
    });
}

function initBanner(banner) {
    const btn = banner.querySelector('.rd-banner__pause');
    if (!btn) return;
    btn.hidden = false;
    btn.addEventListener('click', () => {
        const paused = btn.getAttribute('aria-pressed') !== 'true';
        btn.setAttribute('aria-pressed', String(paused));
        banner.classList.toggle('is-paused', paused);
    });
}

function init() {
    const header = document.querySelector('.site-header');
    if (header) initNav(header);
    document.querySelectorAll('.rd-banner').forEach(initBanner);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
